import { experimentsAreMeaningful } from './item-publish-state';
import type { ItemOutcomePoint, TacticApplication, TacticExperiment } from './cockpit-data';

export type JoinedTimelineEvent = TacticApplication & {
  experiments: TacticExperiment[];
  // True when experiments exist for the application but were withheld because
  // the mutation never reached Amazon (KET-1114). The timeline says so rather
  // than rendering an empty gap.
  experiments_withheld: boolean;
};

export type TacticExperimentPresentation = {
  experimentId: string;
  timeUnderTest: TacticExperiment['time_under_test'];
  timeUnderTestUnit: TacticExperiment['time_under_test_unit'];
  confidence: TacticExperiment['confidence'];
};

// Both the item-timeline chip and the Experiments surface use this projection.
// Keep the backend values typed but otherwise untouched so neither surface can
// grow its own interpretation of trial duration or statistical confidence.
export function tacticExperimentPresentation(
  experiment: TacticExperiment,
): TacticExperimentPresentation {
  return {
    experimentId: experiment.experiment_id,
    timeUnderTest: experiment.time_under_test,
    timeUnderTestUnit: experiment.time_under_test_unit,
    confidence: experiment.confidence,
  };
}

export function runningTacticExperiments(
  experiments: TacticExperiment[],
): TacticExperiment[] {
  return experiments.filter((experiment) => experiment.state.toUpperCase() === 'RUNNING');
}

export function joinTimelineExperiments(
  applications: TacticApplication[],
  experiments: TacticExperiment[],
): JoinedTimelineEvent[] {
  const byApplication = new Map<string, TacticExperiment[]>();
  for (const experiment of experiments) {
    const matches = byApplication.get(experiment.application_id) ?? [];
    matches.push(experiment);
    byApplication.set(experiment.application_id, matches);
  }
  return applications.map((application) => {
    const matches = byApplication.get(application.application_id) ?? [];
    // "Under test: 96h" on a never-published application measures a trial that
    // never started: the negative keyword was never live on Amazon. Withhold
    // the chip instead of rendering a fabricated clock.
    const meaningful = experimentsAreMeaningful(application);
    return {
      ...application,
      experiments: meaningful ? matches : [],
      experiments_withheld: !meaningful && matches.length > 0,
    };
  });
}

export function metricAvailability(points: ItemOutcomePoint[]): {
  profitYield: boolean;
  unitsSold: boolean;
} {
  return {
    profitYield: points.some((point) => point.profit_yield !== null),
    unitsSold: points.some((point) => point.units_sold !== null),
  };
}
